// Lógica pura del simulador de la llave. Sin React.
import { BracketStage, Match, TeamRef } from '../api/client';
import { BracketPicks } from '../stores/store';

export interface Slot {
  team: string | null; // selección que ocupa el slot (null = aún sin definir)
  label: string; // texto si no está definido ("Ganador #88", "1º Grupo J", ...)
  projected: boolean; // proyectado desde un grupo en juego
}

export interface SimGraph {
  matches: Match[];
  byNum: Map<number, Match>;
  consumers: Map<number, number[]>; // num -> partidos que dependen de W{num}/L{num}
}

export function buildGraph(stages: BracketStage[]): SimGraph {
  const matches = stages.flatMap((s) => s.matches);
  const byNum = new Map<number, Match>();
  const consumers = new Map<number, number[]>();

  for (const m of matches) {
    if (m.num != null) byNum.set(m.num, m);
  }
  for (const m of matches) {
    if (m.num == null) continue;
    for (const ref of [m.team1, m.team2]) {
      const mm = /^[WL](\d+)$/.exec(ref.name);
      if (mm) {
        const n = Number(mm[1]);
        const arr = consumers.get(n) ?? [];
        arr.push(m.num);
        consumers.set(n, arr);
      }
    }
  }
  return { matches, byNum, consumers };
}

// Resolutor con memo: calcula participantes/ganador/perdedor de cada partido según los picks.
export function createResolver(g: SimGraph, picks: BracketPicks) {
  const wmemo = new Map<number, string | null>();

  function slot(ref: TeamRef): Slot {
    const w = /^W(\d+)$/.exec(ref.name);
    if (w) return { team: winner(Number(w[1])), label: `Ganador #${w[1]}`, projected: false };
    const l = /^L(\d+)$/.exec(ref.name);
    if (l) return { team: loser(Number(l[1])), label: `Perdedor #${l[1]}`, projected: false };
    return { team: ref.resolved, label: ref.label ?? ref.name, projected: ref.projected };
  }

  function parts(m: Match): [Slot, Slot] {
    return [slot(m.team1), slot(m.team2)];
  }

  // Ganador efectivo: resultado real si ya se jugó; si no, el pick del usuario.
  function winner(num: number): string | null {
    if (wmemo.has(num)) return wmemo.get(num) ?? null;
    wmemo.set(num, null); // corta recursión accidental
    const m = g.byNum.get(num);
    let res: string | null = null;
    if (m) {
      if (m.score && m.score.ft[0] !== m.score.ft[1]) {
        const [p1, p2] = parts(m);
        res = m.score.ft[0] > m.score.ft[1] ? p1.team : p2.team;
      } else {
        res = picks[num] ?? null;
      }
    }
    wmemo.set(num, res);
    return res;
  }

  function loser(num: number): string | null {
    const m = g.byNum.get(num);
    const w = winner(num);
    if (!m || !w) return null;
    const [p1, p2] = parts(m);
    if (p1.team === w) return p2.team;
    if (p2.team === w) return p1.team;
    return null;
  }

  return { parts, winner, loser };
}

// ¿El resultado de este partido está fijado por un resultado real (no editable)?
export function isLocked(m: Match): boolean {
  return m.score != null && m.score.ft[0] !== m.score.ft[1];
}

// Elige ganador y limpia toda la rama que dependía del resultado anterior.
export function pickWinner(num: number, team: string, g: SimGraph, picks: BracketPicks): BracketPicks {
  const next: BracketPicks = { ...picks, [num]: team };
  clearDownstream(num, g, next);
  return next;
}

// Des-elegir el ganador de un partido y limpiar lo que dependía de él.
export function unpickWinner(num: number, g: SimGraph, picks: BracketPicks): BracketPicks {
  const next: BracketPicks = { ...picks };
  delete next[num];
  clearDownstream(num, g, next);
  return next;
}

// Mitad de la llave a la que pertenece un partido: 'A' (lado de la semi #101),
// 'B' (lado de la semi #102), o 'center' (3er puesto y final). Sigue la cadena de ganadores.
export function bracketHalf(num: number, g: SimGraph): 'A' | 'B' | 'center' {
  let cur = num;
  let guard = 0;
  while (cur !== 101 && cur !== 102 && cur !== 103 && cur !== 104 && guard++ < 12) {
    const cons = g.consumers.get(cur);
    if (!cons || cons.length === 0) return 'center';
    cur = cons[0]; // fuera de semis hay un único consumidor (el ganador avanza)
  }
  return cur === 101 ? 'A' : cur === 102 ? 'B' : 'center';
}

function clearDownstream(num: number, g: SimGraph, picks: BracketPicks): void {
  for (const c of g.consumers.get(num) ?? []) {
    if (picks[c] !== undefined) delete picks[c];
    clearDownstream(c, g, picks);
  }
}

// Cantidad de partidos con ganador definido (pick o resultado real).
export function decidedCount(g: SimGraph, picks: BracketPicks): number {
  const r = createResolver(g, picks);
  let n = 0;
  for (const m of g.matches) {
    if (m.num != null && r.winner(m.num)) n++;
  }
  return n;
}
